
import { useEffect, useRef, useState } from 'react';
import NeuralNetworkBg from '../../../components/feature/NeuralNetworkBg';

const results = [
  { text: 'Reduced compulsive behavior', icon: 'ri-shield-check-line', stat: '73%', statLabel: 'reduction' },
  { text: 'Clearer focus', icon: 'ri-eye-line', stat: '2.4x', statLabel: 'improvement' },
  { text: 'Faster stress recovery', icon: 'ri-speed-up-line', stat: '58%', statLabel: 'faster' },
  { text: 'Emotional stability', icon: 'ri-heart-3-line', stat: '81%', statLabel: 'report gains' },
  { text: 'Natural motivation', icon: 'ri-rocket-2-line', stat: '3.1x', statLabel: 'increase' },
  { text: 'Calm baseline', icon: 'ri-mental-health-line', stat: '67%', statLabel: 'improvement' },
];

export default function Results() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="results"
      ref={sectionRef}
      className="relative py-28 lg:py-36 px-6 lg:px-12 overflow-hidden noise-texture"
    >
      {/* Softer dark gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#141118] via-[#1a1520] to-[#141118]"></div>

      {/* Neural networks — warm tones */}
      <NeuralNetworkBg
        color="232,149,106"
        nodeCount={40}
        opacity={0.35}
        speed={0.7}
        variant="dark"
      />

      <NeuralNetworkBg
        color="212,120,158"
        nodeCount={25}
        opacity={0.2}
        speed={0.4}
        variant="dark"
      />

      {/* Soft animated gradient orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute w-[700px] h-[700px] rounded-full animate-float opacity-10"
          style={{
            background:
              'radial-gradient(circle, rgba(232,149,106,0.3) 0%, transparent 70%)',
            top: '-15%',
            left: '30%',
          }}
        ></div>
        <div
          className="absolute w-[500px] h-[500px] rounded-full animate-float-slow opacity-10"
          style={{
            background:
              'radial-gradient(circle, rgba(77,184,199,0.3) 0%, transparent 70%)',
            bottom: '-10%',
            right: '10%',
            animationDelay: '3s',
          }}
        ></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto">
        <div
          className={`text-center mb-20 transition-all duration-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/8 mb-8">
            <div className="w-2 h-2 rounded-full bg-brand-orange animate-pulse"></div>
            <span className="text-xs font-semibold text-brand-orange uppercase tracking-widest">
              Results
            </span>
          </div>

          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 tracking-tight leading-tight">
            What changes <span className="gradient-text-animated">first.</span>
          </h2>

          <p className="text-lg text-white/35 max-w-2xl mx-auto">
            Users of these tools report measurable shifts within the first two weeks of consistent use.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {results.map((result, index) => (
            <div
              key={index}
              className={`group relative p-8 rounded-2xl glass-card hover:bg-white/10 transition-all duration-500 hover:shadow-2xl hover:shadow-brand-orange/8 cursor-pointer ${
                visible ? 'animate-fade-in-up' : 'opacity-0'
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-brand-orange/15 to-brand-pink/15 group-hover:scale-110 transition-transform duration-500">
                  <i className={`${result.icon} text-xl text-brand-orange`}></i>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold gradient-text">{result.stat}</div>
                  <div className="text-xs text-white/25 uppercase tracking-wider">
                    {result.statLabel}
                  </div>
                </div>
              </div>
              <h3 className="text-lg font-bold text-white tracking-tight">{result.text}</h3>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
